import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api, apiErr } from "../lib/api";
import { toast } from "sonner";
import { CalendarCheck, Check, X, MessageSquare, Send, GraduationCap, Plus, Trash2, Video, Paperclip, Loader2, Eye, Inbox, Pencil, CalendarPlus, Search, Clock, Mic, Square, Pin, PinOff, RotateCcw, ScrollText, Download } from "lucide-react";
import { VideoEmbed } from "./VideoEmbed";
import { TrainingRsvp } from "./TrainingRsvp";
import { RsvpRoster } from "./RsvpRoster";
import { fmtStamp } from "../lib/datetime";
import { RecordingWaveform } from "./RecordingWaveform";
import { MessageVoiceRecorder } from "./MessageVoiceRecorder";

function retentionLeft(createdAt, days) {
  if (!createdAt || !days) return null;
  const created = new Date(createdAt).getTime();
  if (isNaN(created)) return null;
  const elapsed = (Date.now() - created) / 86400000;
  return Math.max(0, Math.ceil(days - elapsed));
}

function undoMinsLeft(deleteAt) {
  if (!deleteAt) return null;
  const t = new Date(deleteAt).getTime();
  if (isNaN(t)) return null;
  return Math.max(0, Math.ceil((t - Date.now()) / 60000));
}

const ACTION_META = {
  kept: { label: "Kept", cls: "bg-clinical/10 text-clinical" },
  unkept: { label: "Keep removed", cls: "bg-sand text-muted2" },
  deleted: { label: "Deleted", cls: "bg-amber-100 text-amber-700" },
  restored: { label: "Restored", cls: "bg-calm/15 text-calm" },
  purged: { label: "Purged", cls: "bg-critical/10 text-critical" },
};

function fmtDuration(sec) {
  if (sec == null) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}
import { useAuth } from "../context/AuthContext";

const inp = "w-full border border-line bg-white rounded-md px-3 py-2 text-sm outline-none focus:border-clinical";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const abs = (u) => (u ? (u.startsWith("http") ? u : `${BACKEND}${u}`) : "");

function fmtReqDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

export function AdminRequests({ onSchedule }) {
  const [reqs, setReqs] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const { user } = useAuth();
  const isSysAdmin = user?.role === "system_admin";
  const load = useCallback(async () => { try { const { data } = await api.get("/admin/appointment-requests"); setReqs(data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const setStatus = async (r, status) => {
    try { await api.put(`/admin/appointment-requests/${r.id}/status`, { status }); toast.success(`Marked ${status}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm("Permanently delete this request (and its voice note)?")) return;
    try { await api.delete(`/admin/appointment-requests/${r.id}`); toast.success("Request deleted"); setSelected((s) => s.filter((x) => x !== r.id)); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const bulkDelete = async () => {
    if (!window.confirm(`Permanently delete ${selected.length} request(s)? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/appointment-requests/bulk-delete", { ids: selected }); toast.success(`Deleted ${data.deleted} request(s)`); setSelected([]); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return reqs;
    return reqs.filter((r) => [r.patient_name, r.patient_username, r.patient_code, r.id, fmtReqDate(r.created_at), r.status]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [reqs, search]);
  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2 mb-6"><CalendarCheck className="w-7 h-7 text-clinical" /> Appointment Requests</h1>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="request-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by patient, username, ID, date or status…" className={`${inp} pl-9`} />
          {search && <button data-testid="request-search-clear" onClick={() => setSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted2 hover:text-ink"><X className="w-4 h-4" /></button>}
        </div>
        <span className="text-xs text-muted2">{filtered.length} request(s)</span>
      </div>
      {isSysAdmin && selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-critical/5 border border-critical/20 rounded-md px-3 py-2" data-testid="request-bulk-bar">
          <span className="text-sm font-semibold text-critical">{selected.length} selected</span>
          <button onClick={bulkDelete} data-testid="request-bulk-delete-btn" className="inline-flex items-center gap-1 bg-critical text-white text-sm font-semibold rounded-md px-3 py-1.5"><Trash2 className="w-4 h-4" /> Delete selected</button>
        </div>
      )}
      <div className="grid gap-3" data-testid="admin-requests-list">
        {reqs.length === 0 && <p className="text-muted2">No appointment requests yet.</p>}
        {reqs.length > 0 && filtered.length === 0 && <p className="text-muted2" data-testid="request-no-match">No requests match your search.</p>}
        {filtered.map((r) => (
          <div key={r.id} data-testid="admin-request-row" className="bg-white border border-line rounded-md p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {isSysAdmin && <input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} data-testid="request-select-checkbox" className="mt-1 w-4 h-4 rounded border-line accent-critical cursor-pointer" />}
                <div>
                  <div className="font-heading font-bold text-ink">{r.patient_code || r.patient_username || "—"} — {r.patient_name}</div>
                  <div className="text-sm text-muted2">{r.appt_type} · {r.preferred_specialist_name || "Any clinician"}</div>
                  <div className="text-xs text-muted2 mt-1 inline-flex items-center gap-1.5" data-testid="request-datetime"><Clock className="w-3.5 h-3.5" /> Submitted {fmtReqDate(r.created_at)}</div>
                  <div className="text-[11px] text-muted2/80 mt-0.5">ID: {r.id}</div>
                  {r.reason && <p className="text-sm text-ink mt-1">{r.reason}</p>}
                  <div className="flex items-center gap-3 mt-2">
                    {r.image && <a href={abs(r.image)} target="_blank" rel="noreferrer"><img src={abs(r.image)} alt="" className="w-14 h-14 rounded object-cover border border-line" /></a>}
                    {r.voice_note && <audio src={abs(r.voice_note)} controls controlsList="nodownload noplaybackrate" onContextMenu={(e) => e.preventDefault()} className="h-8" data-testid="admin-request-voice" />}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${r.status === "scheduled" ? "bg-calm/10 text-calm" : r.status === "declined" ? "bg-critical/10 text-critical" : "bg-clinical/10 text-clinical"}`}>{r.status}</span>
                {r.status === "pending" && <>
                  <button onClick={() => onSchedule?.(r)} data-testid="request-schedule-appt-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-clinical rounded-md px-3 py-1.5"><CalendarPlus className="w-4 h-4" /> Schedule appointment</button>
                  <button onClick={() => setStatus(r, "declined")} data-testid="request-decline-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-critical border border-critical/30 rounded-md px-3 py-1.5"><X className="w-4 h-4" /> Decline</button>
                </>}
                {isSysAdmin && <button onClick={() => remove(r)} data-testid="request-delete-btn" className="inline-flex items-center gap-1 text-sm font-semibold text-critical border border-critical/30 rounded-md px-2 py-1.5"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminMessages() {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const loadConvos = useCallback(async () => { try { const { data } = await api.get("/admin/conversations"); setConvos(data); } catch (e) { toast.error(apiErr(e)); } }, []);
  const toggleSelect = (uid) => setSelected((s) => { const n = new Set(s); n.has(uid) ? n.delete(uid) : n.add(uid); return n; });
  const loadThread = useCallback(async (uid) => { try { const { data } = await api.get(`/admin/conversations/${uid}`); setThread(data); } catch (e) { toast.error(apiErr(e)); } }, []);
  useEffect(() => { loadConvos(); }, [loadConvos]);
  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/admin/message-recipients", { params: { q } }); setResults(data); } catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { if (active) loadThread(active.user_id); }, [active, loadThread]);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    try { await api.post("/messages", { body, to_user_id: active.user_id }); setBody(""); loadThread(active.user_id); loadConvos(); } catch (e) { toast.error(apiErr(e)); }
  };
  const sendVoice = async (blob) => {
    if (!active) return;
    const fd = new FormData();
    fd.append("to_user_id", active.user_id);
    fd.append("file", blob, "voice.webm");
    try { await api.post("/messages/voice", fd, { headers: { "Content-Type": "multipart/form-data" } }); loadThread(active.user_id); loadConvos(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePinMsg = async (m) => {
    try {
      const { data } = await api.put(`/admin/conversations/messages/${m.id}/pin`);
      toast.success(data.audio_pinned ? "Voice note kept — it won't auto-delete" : "Auto-delete re-enabled");
      loadThread(active.user_id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const bulkKeep = async () => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/conversations/bulk-keep", { user_ids: Array.from(selected) });
      toast.success(`Kept ${data.updated} voice note${data.updated === 1 ? "" : "s"}`);
      setSelected(new Set()); loadConvos(); if (active) loadThread(active.user_id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };
  const undoDeleteMsg = async (batch) => {
    if (!batch) return;
    try {
      const { data } = await api.post("/admin/conversations/undo-delete-audio", { batch });
      toast.success(`Restored ${data.restored} voice note${data.restored === 1 ? "" : "s"}`);
      loadConvos(); if (active) loadThread(active.user_id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const bulkDelete = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete all voice notes in ${selected.size} selected conversation${selected.size === 1 ? "" : "s"}? Transcripts stay. You'll have 10 minutes to undo.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/conversations/bulk-delete-audio", { user_ids: Array.from(selected) });
      setSelected(new Set()); loadConvos(); if (active) loadThread(active.user_id);
      toast.success(`Deleted ${data.deleted} voice note${data.deleted === 1 ? "" : "s"} — 10 minutes to undo`, {
        duration: 15000, action: { label: "Undo", onClick: () => undoDeleteMsg(data.batch) },
      });
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };
  const startWith = (r) => {
    if (!r) return;
    const existing = convos.find((c) => c.user_id === r.user_id);
    setActive(existing || { user_id: r.user_id, name: r.name, code: r.code || "", role: r.role });
    setSearch(""); setResults([]);
  };
  const roleLabel = (role) => (role === "clinical_specialist" ? "Clinician" : role === "public" ? "Patient" : (role || "").replace("_", " "));
  const label = (c) => (c.role === "clinical_specialist" ? "Clinician" : c.code || "Patient");
  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2 mb-6"><MessageSquare className="w-7 h-7 text-clinical" /> Inbox</h1>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="flex flex-col gap-2 min-w-0" data-testid="admin-conversations">
          <div className="relative">
            <input data-testid="admin-new-message-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="✚ New message — search any user…" className={`${inp} mb-1`} />
            {results.length > 0 && (
              <div data-testid="admin-new-message-results" className="absolute z-20 left-0 right-0 top-full bg-white border border-line rounded-md shadow-lg max-h-72 overflow-y-auto">
                {results.map((r) => (
                  <button key={r.user_id} data-testid="admin-new-message-result" onClick={() => startWith(r)} className="w-full text-left px-3 py-2 hover:bg-clinical/5 border-b border-line last:border-0">
                    <span className="font-semibold text-ink text-sm">{r.name || r.username || "Unnamed"}</span>
                    <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-clinical/10 text-clinical">{roleLabel(r.role)}</span>
                    <p className="text-xs text-muted2 truncate">{[r.username && `@${r.username}`, r.email, r.code].filter(Boolean).join(" · ")}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
          {selected.size > 0 && (
            <div className="flex items-center gap-2 flex-wrap w-full min-w-0 bg-orange-50 border border-orange-200 rounded-md p-2" data-testid="msg-bulk-bar">
              <span className="text-xs font-semibold text-ink" data-testid="msg-selected-count">{selected.size} selected</span>
              <div className="flex-1" />
              <button type="button" disabled={bulkBusy} onClick={bulkKeep} data-testid="msg-bulk-keep"
                className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-3 py-1 border border-clinical/40 text-clinical hover:bg-clinical/5 disabled:opacity-40"><Pin className="w-3 h-3" /> Keep all</button>
              <button type="button" disabled={bulkBusy} onClick={bulkDelete} data-testid="msg-bulk-delete"
                className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-3 py-1 border border-critical/40 text-critical hover:bg-critical/5 disabled:opacity-40"><Trash2 className="w-3 h-3" /> Delete voice</button>
            </div>
          )}
          {convos.length === 0 && <p className="text-muted2 text-sm">No conversations yet.</p>}
          {convos.map((c) => (
            <div key={c.user_id} className={`flex items-stretch border rounded-md transition-colors ${active?.user_id === c.user_id ? "border-clinical bg-clinical/5" : "border-line bg-white hover:border-clinical/40"}`}>
              <label className="flex items-center pl-2.5 cursor-pointer" title="Select conversation">
                <input type="checkbox" data-testid="msg-select-checkbox" className="w-4 h-4 accent-clinical"
                  checked={selected.has(c.user_id)} onChange={() => toggleSelect(c.user_id)} />
              </label>
              <button data-testid="admin-conversation-item" onClick={() => setActive(c)} className="flex-1 text-left p-3 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-ink text-sm truncate">{c.name}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {c.voice_unread && <span data-testid="convo-voice-unread" title="New unheard voice note" className="text-[10px] font-bold bg-clinical/10 text-clinical rounded-full px-1.5 inline-flex items-center gap-0.5"><Mic className="w-2.5 h-2.5" /> Voice</span>}
                    {c.unread > 0 && <span className="text-xs font-bold bg-critical text-white rounded-full px-2">{c.unread}</span>}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${c.role === "clinical_specialist" ? "bg-clinical/10 text-clinical" : "bg-calm/10 text-calm"}`}>{label(c)}</span>
                  <p className="text-xs text-muted2 truncate">{c.last}</p>
                </div>
              </button>
            </div>
          ))}
        </div>
        <div className="md:col-span-2 bg-white border border-line rounded-md p-4 flex flex-col">
          {!active ? <p className="text-muted2 text-sm">Select a conversation or start a new one with a clinician.</p> : <>
            <div className="text-sm font-semibold text-ink mb-2 pb-2 border-b border-line" data-testid="admin-active-recipient">{active.name} {active.role === "clinical_specialist" && <span className="text-xs font-normal text-clinical">· Clinician</span>}</div>
            <div className="max-h-80 overflow-y-auto flex flex-col gap-2 mb-3" data-testid="admin-message-thread">
              {thread.length === 0 && <p className="text-sm text-muted2">No messages yet. Send the first message below.</p>}
              {thread.map((m) => (
                <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from_staff ? "bg-clinical text-white self-end" : "bg-sand self-start"}`}>
                  <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5">{m.from_name}</div>
                  {m.audio_pending_delete ? (
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs" data-testid="admin-msg-pending-delete">
                      <Trash2 className="w-3.5 h-3.5" /> <span className="font-semibold">Voice note deleted</span>
                      <button type="button" onClick={() => undoDeleteMsg(m.audio_delete_batch)} data-testid="admin-msg-undo"
                        className="font-semibold underline underline-offset-2 inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Undo</button>
                    </div>
                  ) : m.audio_url ? (
                    <>
                      <audio controls src={`${process.env.REACT_APP_BACKEND_URL}${m.audio_url}`} className="mt-1 w-full h-9" data-testid="admin-message-voice" />
                      <button type="button" onClick={() => togglePinMsg(m)} data-testid="admin-msg-keep"
                        className="mt-1 text-[10px] font-semibold underline underline-offset-2 opacity-80 hover:opacity-100 inline-flex items-center gap-1">
                        {m.audio_pinned ? <><PinOff className="w-3 h-3" /> Kept — unkeep</> : <><Pin className="w-3 h-3" /> Keep</>}
                      </button>
                    </>
                  ) : m.body}
                  {m.created_at && <div className="text-[10px] opacity-60 mt-1 text-right" data-testid="admin-message-time">{fmtStamp(m.created_at)}</div>}
                </div>
              ))}
            </div>
            <form onSubmit={send} className="flex gap-2">
              <input data-testid="admin-message-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply…" className={inp} />
              <button type="submit" data-testid="admin-message-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 font-semibold"><Send className="w-4 h-4" /></button>
            </form>
            <div className="mt-2">
              <MessageVoiceRecorder testid="admin-msg-voice" onSend={sendVoice} />
            </div>
          </>}
        </div>
      </div>
    </div>
  );
}

export function AdminTraining() {
  const [events, setEvents] = useState([]);
  const blankEv = { title: "", description: "", session_type: "permanent", date: "", video_url: "", audience: "all", materials: [] };
  const [ev, setEv] = useState(blankEv);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const load = useCallback(async () => {
    try { const e = await api.get("/training"); setEvents(e.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addMaterial = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const url = await uploadFile(f); setEv((p) => ({ ...p, materials: [...p.materials, { name: f.name, url }] })); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setUploading(false); }
  };
  const resetForm = () => { setEv(blankEv); setEditingId(null); };
  const submitEvent = async (e) => {
    e.preventDefault();
    const payload = { ...ev, date: ev.session_type === "dated" ? ev.date : "" };
    try {
      if (editingId) { await api.put(`/training/${editingId}`, payload); toast.success("Training updated"); }
      else { await api.post("/training", payload); toast.success("Training added"); }
      resetForm(); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const editEvent = (t) => {
    setEditingId(t.id);
    setEv({ title: t.title || "", description: t.description || "", session_type: t.session_type || (t.date ? "dated" : "permanent"), date: t.date || "", video_url: t.video_url || "", audience: t.audience || "all", materials: t.materials || [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const delEvent = async (id) => { try { await api.delete(`/training/${id}`); if (editingId === id) resetForm(); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div>
      <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2 mb-6"><GraduationCap className="w-7 h-7 text-clinical" /> Training & Education</h1>
      <div className="max-w-3xl">
        <h2 className="font-heading font-bold text-lg text-ink mb-3">{editingId ? "Edit training session" : "Add training session"}</h2>
        <form onSubmit={submitEvent} data-testid="training-form" className="bg-white border border-line rounded-md p-4 grid gap-2 mb-4">
          <input data-testid="training-title" required placeholder="Title" value={ev.title} onChange={(e) => setEv({ ...ev, title: e.target.value })} className={inp} />
          <textarea data-testid="training-desc" rows={2} placeholder="Description" value={ev.description} onChange={(e) => setEv({ ...ev, description: e.target.value })} className={`${inp} resize-y`} />
          <select data-testid="training-session-type" value={ev.session_type} onChange={(e) => setEv({ ...ev, session_type: e.target.value })} className={inp}>
            <option value="permanent">Permanent — always-available virtual education session</option>
            <option value="dated">Date-specific session</option>
          </select>
          {ev.session_type === "dated" && (
            <input data-testid="training-date" type="date" value={ev.date} onChange={(e) => setEv({ ...ev, date: e.target.value })} className={inp} />
          )}
          <input data-testid="training-video-url" placeholder="Video URL (YouTube, Vimeo or direct link)" value={ev.video_url} onChange={(e) => setEv({ ...ev, video_url: e.target.value })} className={inp} />
          <select data-testid="training-audience" value={ev.audience} onChange={(e) => setEv({ ...ev, audience: e.target.value })} className={inp}>
            <option value="all">Everyone (public + patients + staff)</option>
            <option value="patients">Patients &amp; public visitors</option>
            <option value="clinicians">Clinicians / staff only</option>
          </select>
          <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-clinical border border-clinical/30 rounded-md px-3 py-2 cursor-pointer hover:bg-clinical/5 w-fit">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />} Add material
            <input data-testid="training-material" type="file" onChange={addMaterial} className="hidden" />
          </label>
          {ev.materials.map((m, i) => <span key={m.url || m.name || i} className="text-xs text-muted2">📎 {m.name}</span>)}
          <div className="flex gap-2">
            <button type="submit" data-testid="training-add-btn" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md py-2 px-4 font-semibold flex-1">{editingId ? <><Check className="w-4 h-4" /> Update training</> : <><Plus className="w-4 h-4" /> Add training</>}</button>
            {editingId && <button type="button" data-testid="training-cancel-btn" onClick={resetForm} className="inline-flex items-center justify-center gap-1.5 border border-line text-ink rounded-md py-2 px-4 font-semibold">Cancel</button>}
          </div>
        </form>
        <div className="grid gap-2" data-testid="training-list">
          {events.map((t) => (
            <div key={t.id} data-testid="training-row" className={`bg-white border rounded-md p-3 ${editingId === t.id ? "border-clinical ring-1 ring-clinical/30" : "border-line"}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-ink">{t.title}{" "}
                  <span className="text-xs text-muted2">· {(t.session_type || (t.date ? "dated" : "permanent")) === "dated" ? (t.date || "date TBC") : "Always available"}</span>
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  <span data-testid="training-views" className="inline-flex items-center gap-1 text-xs font-semibold text-muted2"><Eye className="w-3.5 h-3.5" /> {t.views || 0}</span>
                  <span data-testid="training-audience-badge" className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${t.audience === "clinicians" ? "bg-alert/20 text-alert" : t.audience === "patients" ? "bg-calm/10 text-calm" : "bg-clinical/10 text-clinical"}`}>
                    {t.audience === "clinicians" ? "Staff only" : t.audience === "patients" ? "Patients & public" : "Everyone"}
                  </span>
                  <button data-testid="training-edit-btn" onClick={() => editEvent(t)} className="text-clinical p-1"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => delEvent(t.id)} className="text-critical p-1"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {t.created_at && <div className="text-xs text-muted2 mt-1 inline-flex items-center gap-1.5" data-testid="training-time"><Clock className="w-3 h-3" /> Posted {fmtStamp(t.created_at)}</div>}
              {t.description && <p className="text-sm text-muted2 mt-1">{t.description}</p>}
              {t.video_url && <VideoEmbed url={t.video_url} testid="training-video-embed" />}
              {t.materials?.map((m, i) => <a key={m.url || i} href={abs(m.url)} target="_blank" rel="noreferrer" className="text-sm text-clinical underline block mt-1">📎 {m.name}</a>)}
              <TrainingRsvp trainingId={t.id} counts={t.rsvp} testid="admin-training-rsvp" />
              <RsvpRoster fetchUrl={`/training/${t.id}/rsvps`} testid="admin-training-roster" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AdminGroupCalls() {
  const [calls, setCalls] = useState([]);
  const [call, setCall] = useState({ title: "", starts_at: "", audience: "all" });
  const load = useCallback(async () => {
    try { const c = await api.get("/group-calls"); setCalls(c.data); } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const createCall = async (e) => {
    e.preventDefault();
    try { await api.post("/group-calls", call); toast.success("Group call created"); setCall({ title: "", starts_at: "", audience: "all" }); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const delCall = async (id) => { try { await api.delete(`/group-calls/${id}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="max-w-3xl">
      <h2 className="font-heading font-bold text-xl text-ink mb-3 flex items-center gap-2"><Video className="w-5 h-5 text-clinical" /> Group calls</h2>
      <form onSubmit={createCall} data-testid="groupcall-form" className="bg-white border border-line rounded-md p-4 grid gap-2 mb-4 sm:grid-cols-2">
        <input data-testid="groupcall-title" required placeholder="Call title" value={call.title} onChange={(e) => setCall({ ...call, title: e.target.value })} className={inp} />
        <input data-testid="groupcall-time" type="datetime-local" value={call.starts_at} onChange={(e) => setCall({ ...call, starts_at: e.target.value })} className={inp} />
        <select data-testid="groupcall-audience" value={call.audience} onChange={(e) => setCall({ ...call, audience: e.target.value })} className={inp}>
          <option value="all">Everyone</option>
          <option value="clinicians">Clinicians only</option>
          <option value="patients">Patients</option>
        </select>
        <button type="submit" data-testid="groupcall-add-btn" className="inline-flex items-center justify-center gap-1.5 bg-clinical text-white rounded-md py-2 font-semibold"><Plus className="w-4 h-4" /> Create group call</button>
      </form>
      <div className="grid gap-2" data-testid="groupcall-list">
        {calls.map((c) => (
          <div key={c.id} data-testid="groupcall-row" className="bg-white border border-line rounded-md p-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="font-semibold text-ink flex items-center gap-1.5"><Video className="w-4 h-4 text-clinical" /> {c.title}</span>
                <span className="text-xs text-muted2">{c.starts_at ? new Date(c.starts_at).toLocaleString() : "Anytime"} · {c.audience}</span>
              </div>
              <button onClick={() => delCall(c.id)} className="text-critical p-1"><Trash2 className="w-4 h-4" /></button>
            </div>
            <TrainingRsvp trainingId={c.id} counts={c.rsvp} endpoint={`/group-calls/${c.id}/rsvp`} testid="admin-groupcall-rsvp" />
            <RsvpRoster fetchUrl={`/group-calls/${c.id}/rsvps`} testid="admin-groupcall-roster" />
          </div>
        ))}
      </div>
    </div>
  );
}


export function AdminEnquiries({ onChange, initialGuestId }) {
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null);
  const [thread, setThread] = useState([]);
  const [body, setBody] = useState("");
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState("");
  const [published, setPublished] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sendingVoice, setSendingVoice] = useState(false);
  const [voiceBlob, setVoiceBlob] = useState(null);
  const [voiceUrl, setVoiceUrl] = useState(null);
  const [recSecs, setRecSecs] = useState(30);
  const [recMax, setRecMax] = useState(30);
  const [waveStream, setWaveStream] = useState(null);
  const [retentionDays, setRetentionDays] = useState(90);
  const recLimitRef = useRef(null);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const micOk = typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia && typeof window !== "undefined" && "MediaRecorder" in window;
  const [search, setSearch] = useState("");
  const searchRef = useRef("");
  const [statusFilter, setStatusFilter] = useState("open");
  const statusRef = useRef("open");
  const [sortBy, setSortBy] = useState("recent");
  const sortRef = useRef("recent");
  const [activeResolved, setActiveResolved] = useState(false);
  const [stats, setStats] = useState(null);
  const [canned, setCanned] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [newCanned, setNewCanned] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showLog, setShowLog] = useState(false);
  const [logRows, setLogRows] = useState([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logAction, setLogAction] = useState("");
  const [logActor, setLogActor] = useState("");
  const [logActors, setLogActors] = useState([]);
  const [logSearch, setLogSearch] = useState("");
  const [logFrom, setLogFrom] = useState("");
  const [logTo, setLogTo] = useState("");
  const [logPage, setLogPage] = useState(0);
  const [logTotal, setLogTotal] = useState(0);
  const LOG_PAGE_SIZE = 50;
  const loadConvos = useCallback(async () => {
    try {
      const q = searchRef.current.trim();
      const params = {};
      if (q) params.q = q;
      if (statusRef.current && statusRef.current !== "all") params.status = statusRef.current;
      if (sortRef.current && sortRef.current !== "recent") params.sort = sortRef.current;
      const { data } = await api.get("/admin/enquiries", { params });
      setConvos(data); onChange?.();
    } catch (e) { toast.error(apiErr(e)); }
  }, [onChange]);
  const loadThread = useCallback(async (gid) => {
    try { const { data } = await api.get(`/admin/enquiries/${gid}`); setThread(data); } catch (e) { toast.error(apiErr(e)); }
  }, []);
  useEffect(() => { loadConvos(); const t = setInterval(loadConvos, 15000); return () => clearInterval(t); }, [loadConvos]);
  useEffect(() => { searchRef.current = search; const t = setTimeout(loadConvos, 350); return () => clearTimeout(t); }, [search, loadConvos]);
  useEffect(() => { statusRef.current = statusFilter; setSelected(new Set()); loadConvos(); }, [statusFilter, loadConvos]);
  useEffect(() => { sortRef.current = sortBy; loadConvos(); }, [sortBy, loadConvos]);
  const loadCanned = useCallback(async () => {
    try { const { data } = await api.get("/admin/canned-replies"); setCanned(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadCanned(); }, [loadCanned]);
  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/enquiries-stats"); setStats(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadStats(); const t = setInterval(loadStats, 30000); return () => clearInterval(t); }, [loadStats]);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      if (data?.voice_max_reply_seconds) setRecMax(data.voice_max_reply_seconds);
      if (data?.voice_note_retention_days) setRetentionDays(data.voice_note_retention_days);
    }).catch(() => {});
  }, []);
  const addCanned = async () => {
    const b = newCanned.trim(); if (!b) return;
    try { await api.post("/admin/canned-replies", { title: "", body: b }); setNewCanned(""); loadCanned(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delCanned = async (id) => {
    try { await api.delete(`/admin/canned-replies/${id}`); loadCanned(); } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { if (active) loadThread(active.guest_id); }, [active, loadThread]);
  useEffect(() => {
    if (!active) return;
    api.get(`/admin/enquiries/${active.guest_id}/publish`).then(({ data }) => setPublished(!!data.published)).catch(() => setPublished(false));
    api.get(`/admin/enquiries/${active.guest_id}/resolve`).then(({ data }) => setActiveResolved(!!data.resolved)).catch(() => setActiveResolved(false));
  }, [active]);
  const toggleResolve = async () => {
    try {
      const { data } = await api.put(`/admin/enquiries/${active.guest_id}/resolve`, { resolved: !activeResolved });
      setActiveResolved(data.resolved);
      toast.success(data.resolved ? "Marked as resolved" : "Reopened");
      loadConvos(); loadStats();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const togglePublish = async () => {
    try {
      const { data } = await api.put(`/admin/enquiries/${active.guest_id}/publish`, { published: !published, title: "Community question" });
      setPublished(data.published);
      toast.success(data.published ? "Published — now visible to the public (anonymously)" : "Unpublished");
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    if (initialGuestId) setActive((a) => (a?.guest_id === initialGuestId ? a : { guest_id: initialGuestId }));
  }, [initialGuestId]);
  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    try { await api.post(`/admin/enquiries/${active.guest_id}/reply`, { body }); setBody(""); loadThread(active.guest_id); loadConvos(); } catch (e) { toast.error(apiErr(e)); }
  };
  const saveEdit = async (m) => {
    if (!editBody.trim()) return toast.error("Message cannot be empty");
    try { await api.put(`/admin/enquiries/messages/${m.id}`, { body: editBody }); toast.success("Reply updated"); setEditId(null); setEditBody(""); loadThread(active.guest_id); loadConvos(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePin = async (m) => {
    try {
      const { data } = await api.put(`/admin/enquiries/messages/${m.id}/pin`);
      toast.success(data.audio_pinned ? "Voice note kept — it won't auto-delete" : "Auto-delete re-enabled for this voice note");
      loadThread(active.guest_id); loadConvos();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const toggleSelect = (gid) => setSelected((s) => { const n = new Set(s); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });
  const bulkKeep = async () => {
    if (!selected.size) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/enquiries/bulk-keep", { guest_ids: Array.from(selected) });
      toast.success(`Kept ${data.updated} voice note${data.updated === 1 ? "" : "s"} across ${selected.size} thread${selected.size === 1 ? "" : "s"}`);
      setSelected(new Set()); loadConvos(); if (active) loadThread(active.guest_id);
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };
  const bulkDeleteAudio = async () => {
    if (!selected.size) return;
    if (!window.confirm(`Delete all voice-note audio in ${selected.size} selected thread${selected.size === 1 ? "" : "s"}? Transcripts are kept. You'll have 10 minutes to undo.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/enquiries/bulk-delete-audio", { guest_ids: Array.from(selected) });
      setSelected(new Set()); loadConvos(); if (active) loadThread(active.guest_id);
      toast.success(`Deleted ${data.deleted} voice note${data.deleted === 1 ? "" : "s"} — 10 minutes to undo`, {
        duration: 15000,
        action: { label: "Undo", onClick: () => undoDelete(data.batch) },
      });
    } catch (e) { toast.error(apiErr(e)); } finally { setBulkBusy(false); }
  };
  const undoDelete = async (batch) => {
    if (!batch) return;
    try {
      const { data } = await api.post("/admin/enquiries/undo-delete-audio", { batch });
      toast.success(`Restored ${data.restored} voice note${data.restored === 1 ? "" : "s"}`);
      loadConvos(); if (active) loadThread(active.guest_id);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const params = { limit: LOG_PAGE_SIZE, offset: logPage * LOG_PAGE_SIZE };
      if (logAction) params.action = logAction;
      if (logActor) params.actor_id = logActor;
      if (logSearch.trim()) params.q = logSearch.trim();
      if (logFrom) params.date_from = logFrom;
      if (logTo) params.date_to = logTo;
      const { data } = await api.get("/admin/audio-deletion-log", { params });
      setLogRows(data.items || []);
      setLogTotal(data.total || 0);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLogLoading(false); }
  }, [logAction, logActor, logSearch, logFrom, logTo, logPage]);
  useEffect(() => { if (!showLog) return; const t = setTimeout(loadLog, 300); return () => clearTimeout(t); }, [showLog, loadLog]);
  // Reset to first page whenever filters/search change.
  useEffect(() => { setLogPage(0); }, [logAction, logActor, logSearch, logFrom, logTo, showLog]);
  useEffect(() => {
    if (!showLog) return;
    api.get("/admin/audio-deletion-log/actors").then(({ data }) => setLogActors(data)).catch(() => {});
  }, [showLog]);
  const openThread = (gid) => { setShowLog(false); setActive({ guest_id: gid }); };
  const exportLog = async (format) => {
    try {
      const params = new URLSearchParams({ format });
      if (logAction) params.set("action", logAction);
      if (logActor) params.set("actor_id", logActor);
      if (logSearch.trim()) params.set("q", logSearch.trim());
      if (logFrom) params.set("date_from", logFrom);
      if (logTo) params.set("date_to", logTo);
      const res = await api.get(`/admin/audio-deletion-log/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = format === "pdf" ? "audio-activity-log.pdf" : "audio-activity-log.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const startVoiceReply = async () => {
    if (recording || sendingVoice || !active) return;
    if (voiceUrl) { URL.revokeObjectURL(voiceUrl); setVoiceUrl(null); setVoiceBlob(null); }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        streamRef.current?.getTracks().forEach((t) => t.stop());
        setWaveStream(null);
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        if (!blob.size) return;
        // Hold for preview — staff can play, redo or discard before sending.
        setVoiceBlob(blob);
        setVoiceUrl(URL.createObjectURL(blob));
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
      setWaveStream(stream);
      setRecSecs(recMax);
      clearInterval(recLimitRef.current);
      recLimitRef.current = setInterval(() => setRecSecs((s) => {
        if (s <= 1) { stopVoiceReply(); return 0; }
        return s - 1;
      }), 1000);
    } catch { setRecording(false); toast.error("Microphone unavailable"); }
  };
  const stopVoiceReply = () => { clearInterval(recLimitRef.current); if (recRef.current && recRef.current.state === "recording") { recRef.current.stop(); } setRecording(false); };
  const discardVoice = () => { if (voiceUrl) URL.revokeObjectURL(voiceUrl); setVoiceUrl(null); setVoiceBlob(null); };
  const sendVoiceReply = async () => {
    if (!voiceBlob || !active) return;
    setSendingVoice(true);
    try {
      const fd = new FormData();
      fd.append("file", voiceBlob, "reply.webm");
      await api.post(`/admin/enquiries/${active.guest_id}/voice-reply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      discardVoice();
      loadThread(active.guest_id); loadConvos();
      toast.success("Voice reply sent");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSendingVoice(false); }
  };
  useEffect(() => () => { streamRef.current?.getTracks().forEach((t) => t.stop()); if (voiceUrl) URL.revokeObjectURL(voiceUrl); }, [voiceUrl]);
  const shortId = (g) => `Visitor ${String(g).slice(0, 6)}`;
  return (
    <div>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h1 className="font-heading font-bold text-3xl tracking-tight text-ink flex items-center gap-2"><Inbox className="w-7 h-7 text-clinical" /> General Enquiries</h1>
        <button type="button" onClick={() => setShowLog(true)} data-testid="open-deletion-log"
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-line bg-white px-3 py-1.5 text-xs font-semibold text-muted2 hover:text-ink hover:border-clinical/40 transition-colors">
          <ScrollText className="w-4 h-4" /> Deletion log
        </button>
      </div>
      <p className="text-sm text-muted2 mb-6">Public visitors' messages from the website chat box. Responded to by our team (Super Admin / System Admin).</p>
      {stats && (
        <div className="flex items-center gap-2 mb-4" data-testid="enquiry-stats">
          <span className="inline-flex items-center gap-1.5 bg-clinical/10 text-clinical text-xs font-bold rounded-full px-3 py-1"><span data-testid="stat-open">{stats.open}</span> open</span>
          <span className="inline-flex items-center gap-1.5 bg-calm/15 text-calm text-xs font-bold rounded-full px-3 py-1"><span data-testid="stat-resolved">{stats.resolved}</span> resolved</span>
          <span className="inline-flex items-center gap-1.5 bg-sand text-muted2 text-xs font-bold rounded-full px-3 py-1"><span data-testid="stat-total">{stats.total}</span> total</span>
          {stats.avg_response_seconds != null && (
            <span className="inline-flex items-center gap-1.5 bg-ink/10 text-ink text-xs font-bold rounded-full px-3 py-1" title="Average time to first team reply">
              <Clock className="w-3 h-3" /> <span data-testid="stat-avg-response">{fmtDuration(stats.avg_response_seconds)}</span> avg reply
            </span>
          )}
        </div>
      )}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="grid gap-2 content-start" data-testid="admin-enquiries-list">
          <div className="flex gap-1 bg-sand rounded-full p-1" data-testid="enquiry-status-filter">
            {[["open", "Open"], ["done", "Done"], ["expiring", "Expiring"], ["all", "All"]].map(([k, label]) => (
              <button key={k} onClick={() => setStatusFilter(k)} data-testid={`enquiry-filter-${k}`}
                className={`flex-1 text-xs font-semibold rounded-full py-1.5 transition-colors ${statusFilter === k ? "bg-white text-clinical shadow-sm" : "text-muted2 hover:text-ink"}`}>{label}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 text-muted2 absolute left-3 top-1/2 -translate-y-1/2" />
            <input data-testid="admin-enquiry-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search enquiries…" className={`${inp} pl-9`} />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="enquiry-sort" className={inp}>
            <option value="recent">Sort: Most recent</option>
            <option value="waiting">Sort: Longest waiting</option>
          </select>
          {statusFilter === "expiring" && convos.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap bg-orange-50 border border-orange-200 rounded-md p-2" data-testid="enquiry-bulk-bar">
              <label className="flex items-center gap-1.5 text-xs font-semibold text-ink cursor-pointer">
                <input type="checkbox" data-testid="enquiry-select-all" className="w-4 h-4 accent-clinical"
                  checked={selected.size > 0 && selected.size === convos.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(convos.map((c) => c.guest_id)) : new Set())} />
                Select all
              </label>
              <span className="text-xs text-muted2" data-testid="enquiry-selected-count">{selected.size} selected</span>
              <div className="flex-1" />
              <button type="button" disabled={!selected.size || bulkBusy} onClick={bulkKeep} data-testid="enquiry-bulk-keep"
                className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-3 py-1 border border-clinical/40 text-clinical hover:bg-clinical/5 disabled:opacity-40">
                <Pin className="w-3 h-3" /> Keep all
              </button>
              <button type="button" disabled={!selected.size || bulkBusy} onClick={bulkDeleteAudio} data-testid="enquiry-bulk-delete"
                className="text-xs font-semibold inline-flex items-center gap-1 rounded-full px-3 py-1 border border-critical/40 text-critical hover:bg-critical/5 disabled:opacity-40">
                <Trash2 className="w-3 h-3" /> Delete audio
              </button>
            </div>
          )}
          {convos.length === 0 && <p className="text-muted2 text-sm">{search ? "No enquiries match your search." : statusFilter === "done" ? "No resolved enquiries." : statusFilter === "expiring" ? "No voice notes are expiring in the next 7 days." : "No open enquiries."}</p>}
          {convos.map((c) => (
            <div key={c.guest_id} className={`flex items-stretch border rounded-md transition-colors ${active?.guest_id === c.guest_id ? "border-clinical bg-clinical/5" : "border-line bg-white hover:border-clinical/40"}`}>
              {statusFilter === "expiring" && (
                <label className="flex items-center pl-2.5 cursor-pointer" title="Select this thread">
                  <input type="checkbox" data-testid="enquiry-select-checkbox" className="w-4 h-4 accent-clinical"
                    checked={selected.has(c.guest_id)} onChange={() => toggleSelect(c.guest_id)} />
                </label>
              )}
              <button data-testid="admin-enquiry-item" onClick={() => setActive(c)} className="flex-1 text-left p-3 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-ink text-sm truncate">{shortId(c.guest_id)}</span>
                  <span className="flex items-center gap-1 shrink-0">
                    {c.expiring && <span data-testid="enquiry-expiring-badge" title="Has a voice note expiring within 7 days" className="text-[10px] font-bold bg-orange-100 text-orange-700 rounded-full px-2 inline-flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> Expiring</span>}
                    {c.awaiting && !c.resolved && <span data-testid="enquiry-awaiting-badge" className="text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full px-2">Awaiting</span>}
                    {c.resolved && <span data-testid="enquiry-done-badge" className="text-[10px] font-bold bg-calm/15 text-calm rounded-full px-2">Done</span>}
                    {c.unread > 0 && <span className="text-xs font-bold bg-critical text-white rounded-full px-2">{c.unread}</span>}
                  </span>
                </div>
                <p className="text-xs text-muted2 truncate mt-0.5">{c.last}</p>
              </button>
            </div>
          ))}
        </div>
        <div className="md:col-span-2 bg-white border border-line rounded-md p-4 flex flex-col">
          {!active ? <p className="text-muted2 text-sm">Select an enquiry to view and reply.</p> : <>
            <div className="text-sm font-semibold text-ink mb-2 pb-2 border-b border-line flex items-center justify-between gap-2" data-testid="admin-enquiry-recipient">
              <span>{shortId(active.guest_id)}</span>
              <div className="flex items-center gap-1.5">
                <button onClick={toggleResolve} data-testid="enquiry-resolve-toggle" className={`text-xs font-bold rounded-full px-3 py-1 border transition-colors ${activeResolved ? "bg-calm text-white border-calm" : "border-line text-muted2 hover:border-calm/50"}`}>
                  {activeResolved ? "Resolved ✓" : "Mark resolved"}
                </button>
                <button onClick={togglePublish} data-testid="enquiry-publish-toggle" className={`text-xs font-bold rounded-full px-3 py-1 border transition-colors ${published ? "bg-clinical text-white border-clinical" : "border-line text-muted2 hover:border-clinical/50"}`}>
                  {published ? "Published ✓ (public)" : "Publish to public"}
                </button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto flex flex-col gap-2 mb-3" data-testid="admin-enquiry-thread">
              {thread.map((m) => (
                <div key={m.id} className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from_public ? "bg-sand self-start" : "bg-clinical text-white self-end"}`}>
                  <div className="text-[10px] uppercase font-bold opacity-70 mb-0.5 flex items-center gap-1.5">
                    {m.from_public ? "Visitor" : m.from_name}{m.auto ? " · auto" : ""}{m.edited_at ? " · edited" : ""}
                    {!m.from_public && !m.auto && editId !== m.id && (
                      <button onClick={() => { setEditId(m.id); setEditBody(m.body); }} data-testid="enquiry-edit-btn" className="ml-1 inline-flex items-center gap-0.5 hover:opacity-100 opacity-90"><Pencil className="w-3 h-3" /></button>
                    )}
                  </div>
                  {editId === m.id ? (
                    <div className="flex flex-col gap-1.5">
                      <textarea rows={2} value={editBody} onChange={(e) => setEditBody(e.target.value)} data-testid="enquiry-edit-input" className="w-full text-ink rounded-md px-2 py-1.5 text-sm outline-none" />
                      <div className="flex gap-1.5">
                        <button onClick={() => saveEdit(m)} data-testid="enquiry-edit-save" className="text-xs font-semibold bg-white text-clinical rounded px-2 py-1">Save</button>
                        <button onClick={() => { setEditId(null); setEditBody(""); }} className="text-xs font-semibold bg-white/20 text-white rounded px-2 py-1">Cancel</button>
                      </div>
                    </div>
                  ) : m.body}
                  {m.audio_pending_delete ? (
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-xs bg-critical/5 border border-critical/20 rounded-md px-2 py-1.5" data-testid="voice-pending-delete">
                      <Trash2 className="w-3.5 h-3.5 text-critical shrink-0" />
                      <span className="text-critical font-semibold">Voice note deleted</span>
                      {undoMinsLeft(m.audio_delete_at) !== null && <span className="opacity-70">· {undoMinsLeft(m.audio_delete_at)}m left to undo</span>}
                      <button type="button" onClick={() => undoDelete(m.audio_delete_batch)} data-testid="voice-undo-btn"
                        className="ml-auto font-semibold underline underline-offset-2 text-clinical hover:opacity-80 inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Undo</button>
                    </div>
                  ) : (
                    <>
                      {m.audio_url && (
                        <audio controls data-testid="admin-enquiry-voice"
                          src={`${process.env.REACT_APP_BACKEND_URL}${m.audio_url}`}
                          className="mt-2 w-full h-9" />
                      )}
                      {m.audio_expired && <div className="text-[10px] italic opacity-60 mt-1">Voice note expired (kept transcript)</div>}
                      {m.audio_url && !m.audio_expired && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {m.audio_pinned ? (
                            <span className="text-[10px] font-semibold opacity-90 inline-flex items-center gap-1" data-testid="voice-kept-badge">
                              <Pin className="w-3 h-3" /> Kept — won't auto-delete
                            </span>
                          ) : (
                            retentionLeft(m.created_at, retentionDays) !== null && (
                              <span className="text-[10px] opacity-70 inline-flex items-center gap-1" data-testid="voice-retention-badge">
                                <Clock className="w-3 h-3" /> Audio expires in {retentionLeft(m.created_at, retentionDays)} day{retentionLeft(m.created_at, retentionDays) === 1 ? "" : "s"}
                              </span>
                            )
                          )}
                          <button type="button" onClick={() => togglePin(m)} data-testid="voice-pin-btn"
                            title={m.audio_pinned ? "Allow this voice note to auto-delete again" : "Keep this voice note so it never auto-deletes"}
                            className="text-[10px] font-semibold underline underline-offset-2 opacity-80 hover:opacity-100 inline-flex items-center gap-1">
                            {m.audio_pinned ? <><PinOff className="w-3 h-3" /> Unkeep</> : <><Pin className="w-3 h-3" /> Keep</>}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {m.created_at && <div className="text-[10px] opacity-60 mt-1 text-right" data-testid="enquiry-message-time">{fmtStamp(m.created_at)}</div>}
                </div>
              ))}
            </div>
            <div className="mb-2" data-testid="canned-replies">
              <div className="flex items-center gap-1.5 flex-wrap">
                {canned.map((c) => (
                  <span key={c.id} className="inline-flex items-center bg-sand border border-line rounded-full text-xs">
                    <button type="button" onClick={() => setBody((b) => (b ? b + " " : "") + c.body)} data-testid="canned-reply-chip"
                      title={c.body} className="pl-3 pr-2 py-1 font-semibold text-ink hover:text-clinical max-w-[180px] truncate">{c.title}</button>
                    {showCanned && (
                      <button type="button" onClick={() => delCanned(c.id)} data-testid="canned-reply-delete" className="pr-2 text-critical hover:opacity-70"><X className="w-3 h-3" /></button>
                    )}
                  </span>
                ))}
                <button type="button" onClick={() => setShowCanned((v) => !v)} data-testid="canned-manage-toggle"
                  className="text-xs font-semibold text-clinical hover:opacity-70 px-2 py-1">{showCanned ? "Done" : "Manage quick replies"}</button>
              </div>
              {showCanned && (
                <div className="flex gap-2 mt-2">
                  <input value={newCanned} onChange={(e) => setNewCanned(e.target.value)} data-testid="canned-new-input"
                    placeholder="New quick reply text…" className={inp} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCanned(); } }} />
                  <button type="button" onClick={addCanned} data-testid="canned-add-btn" className="inline-flex items-center gap-1 bg-clinical text-white rounded-md px-3 text-sm font-semibold shrink-0"><Plus className="w-4 h-4" /> Add</button>
                </div>
              )}
            </div>
            {recording && (
              <div className="mb-2 flex items-center gap-2 rounded-lg border border-critical/30 bg-critical/5 px-2.5 py-1.5" data-testid="voice-reply-recording-bar">
                <span className="inline-block w-2 h-2 rounded-full bg-critical animate-pulse" />
                <RecordingWaveform stream={waveStream} className="flex-1" />
                <span className="text-xs font-bold tabular-nums text-critical">{recSecs}s</span>
              </div>
            )}
            {voiceUrl && (
              <div className="mb-2 flex items-center gap-2 border border-clinical/30 bg-clinical/5 rounded-lg p-2" data-testid="voice-reply-preview">
                <audio controls src={voiceUrl} className="h-9 flex-1" data-testid="voice-reply-preview-player" />
                <button type="button" onClick={startVoiceReply} disabled={sendingVoice} data-testid="voice-reply-redo" title="Re-record" className="inline-flex items-center gap-1 text-xs font-semibold text-muted2 hover:text-ink px-2 py-1"><Mic className="w-4 h-4" /> Redo</button>
                <button type="button" onClick={discardVoice} disabled={sendingVoice} data-testid="voice-reply-discard" title="Discard" className="inline-flex items-center gap-1 text-xs font-semibold text-critical hover:opacity-70 px-2 py-1"><Trash2 className="w-4 h-4" /> Discard</button>
                <button type="button" onClick={sendVoiceReply} disabled={sendingVoice} data-testid="voice-reply-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-50">{sendingVoice ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send voice</button>
              </div>
            )}
            <form onSubmit={send} className="flex gap-2">
              <input data-testid="admin-enquiry-input" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Reply to visitor…" className={inp} />
              {micOk && !voiceUrl && (
                <button type="button" onClick={recording ? stopVoiceReply : startVoiceReply} disabled={sendingVoice}
                  data-testid="admin-enquiry-voice-btn" title={recording ? `Stop (${recSecs}s)` : "Record voice reply"}
                  className={`inline-flex items-center justify-center rounded-md px-3 shrink-0 border transition-colors disabled:opacity-50 min-w-[46px] ${recording ? "bg-critical text-white border-critical animate-pulse" : "bg-white text-clinical border-line hover:border-clinical"}`}>
                  {recording ? <span data-testid="voice-reply-countdown" className="text-xs font-bold tabular-nums">{recSecs}s</span> : <Mic className="w-4 h-4" />}
                </button>
              )}
              <button type="submit" data-testid="admin-enquiry-send" className="inline-flex items-center gap-1.5 bg-clinical text-white rounded-md px-4 font-semibold"><Send className="w-4 h-4" /></button>
            </form>
          </>}
        </div>
      </div>
      {showLog && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="deletion-log-modal" onClick={() => setShowLog(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 p-4 border-b border-line">
              <h2 className="font-heading font-bold text-lg text-ink flex items-center gap-2"><ScrollText className="w-5 h-5 text-clinical" /> Audio Activity Log</h2>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => exportLog("excel")} data-testid="log-export-excel"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold text-muted2 hover:text-ink hover:border-clinical/40"><Download className="w-3.5 h-3.5" /> Excel</button>
                <button type="button" onClick={() => exportLog("pdf")} data-testid="log-export-pdf"
                  className="inline-flex items-center gap-1.5 rounded-md border border-line px-2.5 py-1.5 text-xs font-semibold text-muted2 hover:text-ink hover:border-clinical/40"><Download className="w-3.5 h-3.5" /> PDF</button>
                <button type="button" onClick={() => setShowLog(false)} data-testid="log-close" className="text-muted2 hover:text-ink p-1"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2 px-4 pt-3" data-testid="log-filters">
              <label className="text-xs flex-1 min-w-[160px]">
                <span className="block text-muted2 font-semibold mb-1">Search transcript</span>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 text-muted2 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input type="text" value={logSearch} onChange={(e) => setLogSearch(e.target.value)} data-testid="log-filter-search"
                    placeholder="Keyword in transcript…" className={`${inp} pl-8`} />
                </div>
              </label>
              <label className="text-xs">
                <span className="block text-muted2 font-semibold mb-1">Action</span>
                <select value={logAction} onChange={(e) => setLogAction(e.target.value)} data-testid="log-filter-action" className={inp}>
                  <option value="">All actions</option>
                  <option value="kept">Kept</option>
                  <option value="unkept">Keep removed</option>
                  <option value="deleted">Deleted</option>
                  <option value="restored">Restored</option>
                  <option value="purged">Purged</option>
                </select>
              </label>
              <label className="text-xs">
                <span className="block text-muted2 font-semibold mb-1">Staff member</span>
                <select value={logActor} onChange={(e) => setLogActor(e.target.value)} data-testid="log-filter-actor" className={inp}>
                  <option value="">All staff</option>
                  {logActors.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}{a.role ? ` (${a.role})` : ""}</option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                <span className="block text-muted2 font-semibold mb-1">From</span>
                <input type="date" value={logFrom} onChange={(e) => setLogFrom(e.target.value)} data-testid="log-filter-from" className={inp} />
              </label>
              <label className="text-xs">
                <span className="block text-muted2 font-semibold mb-1">To</span>
                <input type="date" value={logTo} onChange={(e) => setLogTo(e.target.value)} data-testid="log-filter-to" className={inp} />
              </label>
              {(logAction || logActor || logSearch || logFrom || logTo) && (
                <button type="button" onClick={() => { setLogAction(""); setLogActor(""); setLogSearch(""); setLogFrom(""); setLogTo(""); }} data-testid="log-filter-clear"
                  className="text-xs font-semibold text-clinical hover:opacity-70 pb-2">Clear</button>
              )}
            </div>
            <div className="overflow-y-auto p-4">
              {logLoading ? (
                <div className="flex items-center justify-center py-12 text-muted2"><Loader2 className="w-5 h-5 animate-spin" /></div>
              ) : logRows.length === 0 ? (
                <p className="text-sm text-muted2 py-8 text-center">No matching voice-note activity.</p>
              ) : (
                <table className="w-full text-xs" data-testid="deletion-log-table">
                  <thead>
                    <tr className="text-left text-muted2 border-b border-line">
                      <th className="py-2 pr-2 font-semibold">When (UTC)</th>
                      <th className="py-2 pr-2 font-semibold">Action</th>
                      <th className="py-2 pr-2 font-semibold">By</th>
                      <th className="py-2 pr-2 font-semibold">Recorded by</th>
                      <th className="py-2 pr-2 font-semibold">Thread</th>
                      <th className="py-2 font-semibold">Excerpt</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((r) => {
                      const meta = ACTION_META[r.action] || { label: r.action, cls: "bg-sand text-muted2" };
                      return (
                        <tr key={r.id} data-testid="deletion-log-row" onClick={() => openThread(r.guest_id)}
                          title="Open this visitor's thread" className="border-b border-line/60 align-top cursor-pointer hover:bg-clinical/5 transition-colors">
                          <td className="py-2 pr-2 whitespace-nowrap tabular-nums">{(r.at || "").slice(0, 19).replace("T", " ")}</td>
                          <td className="py-2 pr-2"><span className={`inline-block rounded-full px-2 py-0.5 font-semibold ${meta.cls}`}>{meta.label}</span></td>
                          <td className="py-2 pr-2"><span className="font-semibold text-ink">{r.actor_name}</span><br /><span className="text-muted2">{r.actor_role}</span></td>
                          <td className="py-2 pr-2">{r.recorded_by || (r.from_public ? "Visitor (anonymous)" : "—")}</td>
                          <td className="py-2 pr-2 whitespace-nowrap text-clinical font-semibold underline underline-offset-2">{shortId(r.guest_id)}</td>
                          <td className="py-2 max-w-[200px] truncate" title={r.text_excerpt}>{r.text_excerpt || "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
            {logTotal > 0 && (
              <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-t border-line text-xs text-muted2" data-testid="log-pagination">
                <span data-testid="log-page-info">
                  {logTotal === 0 ? "0" : `${logPage * LOG_PAGE_SIZE + 1}–${Math.min((logPage + 1) * LOG_PAGE_SIZE, logTotal)}`} of {logTotal}
                </span>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setLogPage((p) => Math.max(p - 1, 0))} disabled={logLoading || logPage === 0}
                    data-testid="log-prev-page" className="rounded-lg border border-line px-3 py-1 font-medium hover:bg-sand disabled:opacity-40 disabled:cursor-not-allowed">Prev</button>
                  <button type="button" onClick={() => setLogPage((p) => p + 1)} disabled={logLoading || (logPage + 1) * LOG_PAGE_SIZE >= logTotal}
                    data-testid="log-next-page" className="rounded-lg border border-line px-3 py-1 font-medium hover:bg-sand disabled:opacity-40 disabled:cursor-not-allowed">Next</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
